import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SpringPromoBanner } from "@/components/SpringPromo";
import { MobileCallBar } from "@/components/MobileCallBar";

// Eagerly load Home for fastest LCP
import Home from "@/pages/Home";

// Lazy load all other pages to reduce initial bundle size
const ResidentialServices = lazy(() => import("@/pages/ResidentialServices"));
const CommercialServices = lazy(() => import("@/pages/CommercialServices"));
const SnowRemoval = lazy(() => import("@/pages/SnowRemoval"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const LegacyRedirect = lazy(() => import("@/pages/LegacyRedirect"));

// City Landing Pages - lazy loaded
const CityPage = lazy(() => import("@/pages/CityPage"));

// Service Landing Pages - lazy loaded
const LandscapeServices = lazy(() => import("@/pages/services/LandscapeServices"));
const LandscapeDesign = lazy(() => import("@/pages/services/LandscapeDesign"));
const HardscapeServices = lazy(() => import("@/pages/services/HardscapeServices"));
const LawnMaintenance = lazy(() => import("@/pages/services/LawnMaintenance"));
const TreeService = lazy(() => import("@/pages/services/TreeService"));
const PowerSweeping = lazy(() => import("@/pages/services/PowerSweeping"));

// Fixed-position loading overlay - prevents CLS by not affecting page layout
const PageLoader = () => (
  <div 
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 9999,
      background: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App font-manrope" data-testid="app-container">
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin Dashboard - Standalone without site layout */}
                <Route path="/admin" element={<AdminDashboard />} />
                
                {/* Main Site Routes - with navbar/footer */}
                <Route path="/*" element={<MainLayout />} />
              </Routes>
            </Suspense>
            <Toaster 
              position="top-right" 
              richColors 
              toastOptions={{
                className: 'font-manrope',
              }}
            />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

// Main Layout Component with Navbar and Footer
function MainLayout() {
  return (
    <>
      <SpringPromoBanner />
      <Navbar />
      {/* Mobile-safe padding for MobileCallBar: 56px bar + safe-area-inset-bottom + 16px */}
      <main className="mobile-callbar-safe-area md:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/residential" element={<ResidentialServices />} />
          <Route path="/commercial" element={<CommercialServices />} />
          <Route path="/snow-removal" element={<SnowRemoval />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/thank-you" element={<ThankYou />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Service Landing Pages */}
          <Route path="/services" element={<LandscapeServices />} />
          <Route path="/services/landscape" element={<LandscapeServices />} />
          <Route path="/services/landscape-design" element={<LandscapeDesign />} />
          <Route path="/services/hardscape" element={<HardscapeServices />} />
          <Route path="/services/lawn-maintenance" element={<LawnMaintenance />} />
          <Route path="/services/tree-service" element={<TreeService />} />
          <Route path="/services/power-sweeping" element={<PowerSweeping />} />
          
          {/* City Landing Pages */}
          <Route path="/landscaping-new-berlin-wi" element={<CityPage />} />
          <Route path="/landscaping-brookfield-wi" element={<CityPage />} />
          <Route path="/landscaping-menomonee-falls-wi" element={<CityPage />} />
          <Route path="/landscaping-waukesha-wi" element={<CityPage />} />
          <Route path="/landscaping-muskego-wi" element={<CityPage />} />
          <Route path="/landscaping-milwaukee-wi" element={<CityPage />} />
          <Route path="/landscaping-greenfield-wi" element={<CityPage />} />
          
          {/* Legacy URL Redirects (from previous site) */}
          <Route path="/contact-us" element={<LegacyRedirect to="/contact" canonicalUrl="https://waybetterlandandsnow.com/contact" />} />
          <Route path="/contact-us/" element={<LegacyRedirect to="/contact" canonicalUrl="https://waybetterlandandsnow.com/contact" />} />
          <Route path="/best-time-to-cancel-a-lawn-mowing-service-in-wisconsin" element={<LegacyRedirect to="/blog" canonicalUrl="https://waybetterlandandsnow.com/blog" />} />
          <Route path="/best-time-to-cancel-a-lawn-mowing-service-in-wisconsin/" element={<LegacyRedirect to="/blog" canonicalUrl="https://waybetterlandandsnow.com/blog" />} />
          
          {/* 404 Catch-All Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <MobileCallBar />
    </>
  );
}

export default App;
